/**
 * Ritual Update Notification Component
 * Displays a notification when a ritual organizer posts an update
 */

import { EllipsisVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { getAssetUrl } from "@/utils/assets"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"

interface RitualUpdateNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
  onNotificationClick?: (notification: Notification) => void
}

interface RitualUpdateNotificationData {
  ritualId?: string
  ritualName?: string
  organizerId?: string
  organizerName?: string
  messagePreview?: string
  commentId?: string
  ritualCoverImageUrl?: string | null
}

export function RitualUpdateNotification({
  notification,
  onRead,
  onUnread,
  onNotificationClick,
}: RitualUpdateNotificationProps) {
  const data = notification.data as RitualUpdateNotificationData
  const triggeredByUser = notification.triggeredByUser

  const ritualId = data.ritualId
  const ritualName = data.ritualName || "a ritual"
  const organizerId = data.organizerId || triggeredByUser?.id
  const organizerName = data.organizerName || triggeredByUser?.name || "Someone"
  const messagePreview = data.messagePreview
  const isRead = notification.isRead

  const relativeTime = formatNotificationTime(notification.createdAt)

  const handleRitualClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!ritualId) return
    window.location.href = `/rituals/${ritualId}`
    if (!isRead) {
      onRead?.()
    }
  }

  const handleOrganizerClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!organizerId) return
    window.location.href = `/profile/${organizerId}`
    if (!isRead) {
      onRead?.()
    }
  }

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md cursor-pointer transition-colors border ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
      onClick={() => {
        onRead?.()
        onNotificationClick?.(notification)
      }}
    >
      {/* Header Row: Logo, Brand, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* Logo */}
          <img
            src={getAssetUrl("Avatar.png")}
            alt="Rituality"
            className="w-12 h-12 rounded-full object-cover"
          />

          {/* Brand Name and Message */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">Rituality</span>
              <span className="text-stone text-[14px]">@rituality</span>
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row - Organizer name, ritual name */}
            <div className="text-[14px] text-primary leading-[25px]">
              <button
                type="button"
                onClick={handleOrganizerClick}
                className="font-bold cursor-pointer bg-transparent border-none p-0 text-primary"
              >
                {organizerName}
              </button>
              {" posted an update in "}
              <button
                type="button"
                onClick={handleRitualClick}
                className="font-bold cursor-pointer bg-transparent border-none p-0 text-primary"
              >
                {ritualName}
              </button>
            </div>

            {/* Message Preview - italic, not bold */}
            {messagePreview && (
              <div className="text-[14px] italic text-primary/80 leading-[25px] mt-1">
                {messagePreview}
              </div>
            )}
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-2 -right-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  )
}
